import React, { useState, useEffect, useContext } from 'react';
import { Form } from 'antd';
import { useForm } from 'antd/es/form/Form';


import { LoanContext } from '../../LoanDetails';
import { LimitFiles } from '../constants';
import { useCreateLoanDoc, useDeleteFileLoanDoc } from 'services/loan/mutations';
import { useGetLoanDocByLoan } from 'services/loan/querys';
import { useGetTasksByLoan } from 'services/task/querys';
import { LoanDocState, ReplicationStrategy } from 'gql/graphql';
import CardUploadResponse from 'components/BorrowerTask/components/Atoms/Cards/CardUploadResponse/CardUploadResponse';
import InputBorderNone from 'components/BorrowerTask/components/Atoms/Inputs/InputBorderNone/InputBorderNone';

interface ICreditReportFormProps {
  selectLoanDoc: any;
  loanDocs?: any[];
}

/**
 * Formulario específico para Credit Report
 * Este documento tiene campos especiales de metadata:
 * - Credit Score
 * - Latest Credit Report Ordered Date
 * - Credit Report Expiry
 */
const CreditReportForm: React.FC<ICreditReportFormProps> = ({ selectLoanDoc, loanDocs }) => {
  const [form] = useForm();
  const loanSelected: any = useContext(LoanContext);

  const { execute: createLoanDoc, fetching } = useCreateLoanDoc();
  const { execute: deleteFileLoanDoc } = useDeleteFileLoanDoc();
  const { reexecute: getLoanDocByLoan } = useGetLoanDocByLoan({ 
    variables: { loanId: loanSelected?._id }, 
    pause: true 
  });
  const { reexecute: getTasksByLoan } = useGetTasksByLoan({
    variables: { loanId: loanSelected?._id },
    pause: true
  });

  const [fileLoanDoc, setFileLoanDoc] = useState<any[] | undefined>(undefined);

  const isCreditReportDocument = selectLoanDoc?.label?.includes('Credit Report');

  const isLoanDoc = loanDocs?.find(item => 
    item.key === selectLoanDoc?.key || item._id === selectLoanDoc?._id
  );

  useEffect(() => {
    if (selectLoanDoc) {
      form.setFieldValue("name", selectLoanDoc.label);
    }
  }, [selectLoanDoc, form]);

  useEffect(() => {
    const found = loanDocs?.find(item =>
      item.key === selectLoanDoc?.key || item._id === selectLoanDoc?._id
    );
    let initial: any[] | undefined = found?.file && found?.files?.length === 0 
      ? [found.file] 
      : found?.files ? found.files : undefined;

    setFileLoanDoc(initial);
  }, [loanDocs, selectLoanDoc, loanSelected.onboarding]);

  const onFinish = async (file: any, metadata?: any) => {
    const payload = {
      ...form.getFieldsValue(),
      _id: isLoanDoc?._id,
      key: selectLoanDoc?.key,
      loan: loanSelected._id,
      state: LoanDocState.Approved, // Credit Report va directo a Approved
      replicationStrategy: ReplicationStrategy.CopyToAll
    };

    const isFilesNews = file && file?.filter((item: any) => !!item.size && !item.url && !item.key)?.length > 0;

    if (isFilesNews) {
      if (file?.length > 1) {
        payload.files = isLoanDoc?.files?.length > 0 ? null : file;
      } else {
        payload.file = isLoanDoc?.file ? null : file[0];
      }
    }

    if (isCreditReportDocument && metadata) {
      payload.metadata = metadata;
    }

    await createLoanDoc(payload);
    
    setFileLoanDoc(undefined);
    
    await Promise.all([
      getLoanDocByLoan({ requestPolicy: 'network-only' }),
      getTasksByLoan({ requestPolicy: 'network-only' })
    ]);
  };

  const onDeleteFile = async (fileToDelete: any) => {
    if (isLoanDoc && isLoanDoc.file) {
      await deleteFileLoanDoc({ fileKey: isLoanDoc.file.key, loanDocId: isLoanDoc._id });
      setFileLoanDoc([]);
    }
    if (isLoanDoc && isLoanDoc.files && isLoanDoc.files.length > 0 && fileToDelete?.key) {
      await deleteFileLoanDoc({ fileKey: fileToDelete.key, loanDocId: isLoanDoc._id, multipleFiles: true });
      setFileLoanDoc((prev) => prev?.filter((file) => file.key !== fileToDelete.key));
    }
    
    await Promise.all([
      getLoanDocByLoan({ requestPolicy: 'network-only' }),
      getTasksByLoan({ requestPolicy: 'network-only' })
    ]);
  };

  const disabledMultipleDocs = fileLoanDoc && fileLoanDoc?.length > 0;

  return (
    <Form
      form={form}
      name="credit-report-form"
      className='form'
      autoComplete="off"
      onFinish={onFinish}
    >
      <CardUploadResponse
        title=""
        titleLocation='outside'
        limitFiles={LimitFiles.DEFAULT}
        actionDeleteFile={onDeleteFile}
        files={fileLoanDoc}
        submit={{
          title: 'Publish to borrower',
          disabled: disabledMultipleDocs,
          loading: fetching,
          modalConfirm: true,
          confirmDescription: "After submitting, you'll have to contact us to make any changes.",
          onClick: (files, metadata) => onFinish(files, metadata),
          location: 'outside'
        }}
        isCreditReportDocument={isCreditReportDocument}
        isLoanDoc={isLoanDoc}
        hasDropdown={false}
        insertHtml={
          <div className='doc-info'>
            <Form.Item
              name="name"
              className='form-item-doc-name'
              style={{ marginBottom: 0 }}
              rules={[
                {
                  required: true,
                  message: 'Please input your doc name!'
                }
              ]}
            >
              <InputBorderNone
                placeholder="Enter doc name"
                className="form-task-name"
                color="bold"
                disabled={true}
              />
            </Form.Item>
          </div>
        }
        messageErrorDisabled='Please upload a file'
        isSelectChanged={true}
      />
    </Form>
  );
};

export default CreditReportForm;